import { supabase } from "@/integrations/supabase/client";

type LogArgs = {
  /** Server error code (e.g. "page_blocked", "rate_limit") or "unknown". */
  code?: string;
  /** Where the error originated — usually "url" or "image". */
  source: string;
  /** Optional raw Error.message — truncated server-side and via guard trigger. */
  message?: string | null;
  /** Optional Error.stack — truncated to keep the table small. */
  stack?: string | null;
};

/**
 * Fire-and-forget client error logging. No PII — only the code, source,
 * current path, and (when present) the raw error message + stack so we can
 * actually diagnose render crashes instead of guessing. Failures are
 * swallowed so logging can never break the UI.
 */
const sanitizeIdent = (value: string, maxLen: number): string => {
  const cleaned = value
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, maxLen);
  return cleaned || "unknown";
};

const truncate = (value: string | null | undefined, maxLen: number): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.length > maxLen ? trimmed.slice(0, maxLen) : trimmed;
};

export const logErrorEvent = ({ code, source, message, stack }: LogArgs): void => {
  try {
    const path =
      typeof window !== "undefined" && window.location
        ? window.location.pathname.slice(0, 512)
        : null;

    const payload: Record<string, unknown> = {
      code: sanitizeIdent(code || "unknown", 64),
      source: sanitizeIdent(source, 32),
      path,
    };
    const m = truncate(message, 1000);
    if (m) payload.message = m;
    const s = truncate(stack, 4000);
    if (s) payload.stack = s;

    void supabase
      .from("error_events" as any)
      .insert(payload)
      .then(({ error }) => {
        if (error) {
          // Quiet — analytics must never bother the user.
          // Common cause now: anonymous visitor (RLS requires auth).
          console.debug("error_events insert failed", error);
        }
      });
  } catch (err) {
    console.debug("logErrorEvent threw", err);
  }
};
